import React, { useState, useEffect } from 'react';
import { Shield, Lock, AlertTriangle, CheckCircle, XCircle, Database, TestTube } from 'lucide-react';
import { checkDataMigrationNeeded, migrateDataToEncrypted, testEncryption, backupData } from '../utils/dataMigration.js';
import { getDatabase } from '../db/index.js';

const SecurityDashboard = ({ onClose }) => {
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);

  useEffect(() => {
    checkMigrationStatus();
  }, []);

  const checkMigrationStatus = async () => {
    try {
      const db = await getDatabase();
      const status = await checkDataMigrationNeeded(db);
      setMigrationStatus(status);
    } catch (error) {
      console.error('Error checking migration status:', error);
    }
  };

  const runEncryptionTest = async () => {
    setIsLoading(true);
    try {
      const results = await testEncryption();
      setTestResults(results);
    } catch (error) {
      setTestResults({ success: false, error: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const createBackup = async () => {
    setIsLoading(true);
    try {
      const db = await getDatabase();
      const backup = await backupData(db);
      setBackupStatus(backup);
    } catch (error) {
      setBackupStatus({ success: false, error: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const migrateData = async () => {
    setIsLoading(true);
    try {
      const db = await getDatabase();
      const result = await migrateDataToEncrypted(db);
      if (result.success) {
        alert(`Migration successful! Migrated ${result.migratedCount} projects.`);
        checkMigrationStatus();
      } else {
        alert(`Migration failed: ${result.error}`);
      }
    } catch (error) {
      alert(`Migration error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[var(--bg-secondary)] rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Shield className="text-[var(--accent-primary)]" />
            Security Dashboard
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <XCircle size={24} />
          </button>
        </div>

        {/* Security Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-[var(--bg-tertiary)] p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="text-green-500" size={20} />
              <h3 className="font-medium text-white">HTTPS/SSL</h3>
            </div>
            <p className="text-sm text-gray-300">All traffic encrypted</p>
          </div>

          <div className="bg-[var(--bg-tertiary)] p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="text-green-500" size={20} />
              <h3 className="font-medium text-white">Security Headers</h3>
            </div>
            <p className="text-sm text-gray-300">XSS and clickjacking protection</p>
          </div>

          <div className="bg-[var(--bg-tertiary)] p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="text-green-500" size={20} />
              <h3 className="font-medium text-white">Firebase Auth</h3>
            </div>
            <p className="text-sm text-gray-300">Secure authentication</p>
          </div>

          <div className="bg-[var(--bg-tertiary)] p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="text-yellow-500" size={20} />
              <h3 className="font-medium text-white">Data Encryption</h3>
            </div>
            <p className="text-sm text-gray-300">Ready to enable</p>
          </div>
        </div>

        {/* Migration Status */}
        {migrationStatus && (
          <div className="bg-[var(--bg-tertiary)] p-4 rounded-lg mb-6">
            <h3 className="font-medium text-white mb-3 flex items-center gap-2">
              <Database size={20} />
              Data Migration Status
            </h3>
            <div className="text-sm text-gray-300 space-y-1">
              <p>Total Projects: {migrationStatus.projectCount}</p>
              <p>Unencrypted Projects: {migrationStatus.unencryptedCount}</p>
              <p>Needs Migration: {migrationStatus.needsMigration ? 'Yes' : 'No'}</p>
            </div>
          </div>
        )}

        {/* Test Results */}
        {testResults && (
          <div className="bg-[var(--bg-tertiary)] p-4 rounded-lg mb-6">
            <h3 className="font-medium text-white mb-3 flex items-center gap-2">
              <TestTube size={20} />
              Encryption Test Results
            </h3>
            <div className="text-sm">
              {testResults.success ? (
                <div className="text-green-400 flex items-center gap-2">
                  <CheckCircle size={16} />
                  Encryption test passed successfully
                </div>
              ) : (
                <div className="text-red-400 flex items-center gap-2">
                  <XCircle size={16} />
                  Encryption test failed: {testResults.error}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Backup Status */}
        {backupStatus && (
          <div className="bg-[var(--bg-tertiary)] p-4 rounded-lg mb-6">
            <h3 className="font-medium text-white mb-3">Backup Status</h3>
            <div className="text-sm text-gray-300">
              {backupStatus.success ? (
                <div className="text-green-400">
                  Backup created successfully! ({backupStatus.projectCount} projects)
                </div>
              ) : (
                <div className="text-red-400">
                  Backup failed: {backupStatus.error}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={runEncryptionTest}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
          >
            <TestTube size={16} />
            Test Encryption
          </button>

          <button
            onClick={createBackup}
            disabled={isLoading}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
          >
            <Database size={16} />
            Create Backup
          </button>

          {migrationStatus?.needsMigration && (
            <button
              onClick={migrateData}
              disabled={isLoading}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
            >
              <Lock size={16} />
              Migrate Data
            </button>
          )}

          <button
            onClick={() => window.open('https://app.netlify.com', '_blank')}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
          >
            <Shield size={16} />
            Netlify Dashboard
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-900 bg-opacity-20 rounded-lg">
          <h4 className="font-medium text-white mb-2">Safe Security Rollout:</h4>
          <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside">
            <li>Test encryption functionality</li>
            <li>Create backup of existing data</li>
            <li>Migrate existing data to encrypted format</li>
            <li>Enable encryption in database settings</li>
            <li>Monitor for any issues</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboard; 